import { useEffect, useState } from "react";
import {
  FaPlus,
  FaTrash,
  FaLink,
  FaUpload,
  FaFacebook,
  FaTwitter,
  FaInstagram,
  FaLinkedin,
  FaYoutube,
  FaTiktok,
} from "react-icons/fa";
import axios from "axios";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import { ToastContainer, toast } from "react-toastify";

const ImageUpload = ({ label, file, setFile }) => {
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const handleFileUpload = (event) => {
    const selectedFile = event.target.files[0];
    setFile(selectedFile);
  };

  const removeImage = () => {
    setFile(null);
  };

  const imageSrc =
    typeof file === "string"
      ? `https://api.muktihospital.com${file.startsWith("/") ? file : `/${file}`}`
      : file
      ? URL.createObjectURL(file)
      : null;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">{label}</label>
      {imageSrc && (
        <div className="mt-4 relative inline-block">
          <img
            src={imageSrc}
            alt="Uploaded"
            className="border rounded-md w-40 h-32 object-cover bg-gray-200"
          />
          <button
            type="button"
            onClick={removeImage}
            className="absolute -top-2 -right-2 bg-red-500 text-white p-1 rounded-full"
          >
            <FaTrash size={10}/>
          </button>
        </div>
      )}
      <div className="mt-4 w-full">
        <p className="text-[10px] text-gray-600 mt-1">
          Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
        </p>
        <input
          type="file"
          accept="image/*"
          className="border p-2 rounded w-full"
          id={`upload-${label}`}
          onChange={handleFileUpload}
        />
      </div>
    </div>
  );
};

const Footer = () => {
  const [language, setLanguage] = useState("en");
  const [footerLogo, setFooterLogo] = useState(null);
  const [description, setDescription] = useState("");
  const [contact, setContact] = useState({ logo: null, title: "", phone: "" });
  const [sections, setSections] = useState({
    QuickLinks: { title: "", links: [{ label: "", url: "" }] },
    Treatments: { title: "", links: [{ label: "", url: "" }] },
    PatientCare: { title: "", links: [{ label: "", url: "" }] },
    Diagnostic: { title: "", links: [{ label: "", url: "" }] },
  });
  const [copyright, setCopyright] = useState("");
  const [socialLinks, setSocialLinks] = useState({
    facebook: "",
    tiktok: "",
    twitter: "",
    // instagram: "",
    // linkedin: "",
    youtube: "",
  });
  const [listItems, setListItems] = useState([{ label: "", url: "" }]);

  // Define section labels as required.
  const SECTION_LABELS = {
    QuickLinks: "Quick Links",
    Treatments: "Treatments",
    PatientCare: "Advanced Treatments",
    Diagnostic: "Diagnostic",
  };

  // Define a fixed order for the sections.
  const sectionOrder = ["QuickLinks", "Treatments", "PatientCare", "Diagnostic"];

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const res = await axios.get("https://api.muktihospital.com/api/footer");
        const data = res.data?.translations?.[language];
        if (data) {
          setFooterLogo(data.footerLogo || null);
          setDescription(data.description || "");
          setContact(data.contact || { logo: null, title: "", phone: "" });
          setSections(data.sections || sections);
          setCopyright(data.copyright || "");
          setSocialLinks(data.socialLinks || {});
          setListItems(data.listItems || [{ label: "", url: "" }]);
        }
      } catch (err) {
        console.error("Failed to fetch footer data", err);
      }
    };
    fetchFooter();
  }, [language]);

  const handleSectionChange = (section, index, field, value) => {
    const updatedLinks = [...sections[section].links];
    updatedLinks[index][field] = value;
    setSections({
      ...sections,
      [section]: { ...sections[section], links: updatedLinks },
    });
  };

  const addSectionLink = (section) => {
    setSections({
      ...sections,
      [section]: {
        ...sections[section],
        links: [...sections[section].links, { label: "", url: "" }],
      },
    });
  };

  const removeSectionLink = (section, index) => {
    const updatedLinks = sections[section].links.filter((_, i) => i !== index);
    setSections({
      ...sections,
      [section]: { ...sections[section], links: updatedLinks },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("language", language);
    formData.append("description", description);
    formData.append("copyright", copyright);
    formData.append("socialLinks", JSON.stringify(socialLinks));
    formData.append("sections", JSON.stringify(sections));
    formData.append("listItems", JSON.stringify(listItems));

    if (footerLogo instanceof File) {
      formData.append("footerLogo", footerLogo);
    }
    if (contact.logo instanceof File) {
      formData.append("contactLogo", contact.logo);
    }
    formData.append("contactTitle", contact.title);
    formData.append("contactPhone", contact.phone);

    try {
      const res = await axios.post("https://api.muktihospital.com/api/footer", formData);
      toast.success("Footer updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update footer");
    }
  };

  
       // ----------------------------------------------------------------
  //  B) Language Switch (English <-> Bangla)
  // ----------------------------------------------------------------
  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'bn', label: 'Bangla' }
  ];

  const handleLanguageChange = (selectedOption) => {
    setLanguage(selectedOption.value);
  };

     // ─────────────────────────────────────────────────────────────────────────────
  // Render form
  // ─────────────────────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: 'Dashboard', url: '/' },
    { label: 'Manage Sections'},
    { label: 'Footer' }
  ];


  return (
    <div className="bg-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />
      <PageHeading title="Manage Footer" breadcrumbs={breadcrumbs} />
        {/* Language Selection */}
    <div className="mb-6 flex">
      <DynamicSelect
          options={languageOptions}  // Pass options for languages
          value={languageOptions.find(option => option.value === language)}
          onChange={handleLanguageChange}
          isSearchable={false}
          placeholder="Select a language"
        />
    </div>
      <form onSubmit={handleSubmit}>
        {/* Footer Logo */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Footer Logo
            </h2>
          </div>
          <div className="p-5">
            <ImageUpload
              label="Upload Footer Logo"
              file={footerLogo}
              setFile={setFooterLogo}
            />
          </div>
        </div>

        {/* Footer Description */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
            Footer Description
            </h2>
          </div>
          <div className="p-5">
            <textarea
              className="input-field"
              rows="3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter footer description..."
            />
          </div>
        </div>

        {/* Contact Section */}
        <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
              <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Contact
              </h2>
          </div>
        <div className="p-5">
        <ImageUpload
            label="Upload Contact Logo"
            file={contact.logo}
            setFile={(file) => setContact({ ...contact, logo: file })}
          />
          <input
            type="text"
            className="input-field mt-2"
            value={contact.title}
            onChange={(e) => setContact({ ...contact, title: e.target.value })}
            placeholder="Enter contact title"
          />
          <input
            type="tel"
            className="input-field mt-2"
            value={contact.phone}
            onChange={(e) => setContact({ ...contact, phone: e.target.value })}
            placeholder="Enter phone number"
          />
          {contact.phone && (
            <a href={`tel:${contact.phone}`} className="block mt-2 text-blue-400">
              {contact.phone}
            </a>
          )}
        </div>
        </div>

        {/* Dynamic Sections in Fixed Order */}
        <div className="space-y-6 mt-6">
          {sectionOrder.map((section) => (
            <div
              key={section}
              className="bg-white shadow-sm rounded-lg mt-5"
            >
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                {SECTION_LABELS[section]}
                </h2>
            </div>
            <div className="p-5">
                {/* Section Title */}
                <input
                type="text"
                className="input-field"
                value={sections[section].title}
                onChange={(e) =>
                  setSections({
                    ...sections,
                    [section]: {
                      ...sections[section],
                      title: e.target.value,
                    },
                  })
                }
                placeholder="Enter section title"
              />

              {/* Links within this section */}
              {sections[section].links.map((link, index) => (
                <div key={index} className="mt-2 flex items-center gap-2">
                  <input
                    type="text"
                    className="input-field"
                    value={link.label}
                    onChange={(e) =>
                      handleSectionChange(section, index, "label", e.target.value)
                    }
                    placeholder="Link Label"
                  />
                  <input
                    type="text"
                    className="p-2 border rounded-md w-1/2"
                    value={link.url}
                    onChange={(e) =>
                      handleSectionChange(section, index, "url", e.target.value)
                    }
                    placeholder="URL"
                  />
                  {link.url && (
                    <a
                      href={link.url}
                      className="text-blue-400"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FaLink size={12}/>
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => removeSectionLink(section, index)}
                    className="text-red-500"
                  >
                    <FaTrash size={12}/>
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addSectionLink(section)}
                className="mt-3 text-blue-600 text-sm hover:text-blue-700 flex items-center gap-1"
              >
                <FaPlus size={12}/> Add More Links
              </button>
            </div>
            </div>
          ))}
        </div>

        {/* Copyright & Additional Sections */}
        <div className="space-y-6 mt-6">
          <div className="bg-white shadow-sm rounded-lg mt-5">
          <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                Copyright
                </h2>
            </div>
           <div className="p-5">
           <input
              type="text"
              className="input-field"
              value={copyright}
              onChange={(e) => setCopyright(e.target.value)}
              placeholder="Enter copyright text"
            />
           </div>
          </div>

          {/* Social Links */}
          <div className="bg-white shadow-sm rounded-lg mt-5">
            <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Social Links
                  </h2>
              </div>
            <div className="p-5">
            {Object.entries(socialLinks).map(([platform, url]) => (
              <div key={platform} className="flex items-center gap-2 mt-2">
                {platform === "facebook" && (
                  <FaFacebook className="text-blue-600" size={20} />
                )}
                {platform === "twitter" && (
                  <FaTiktok className="text-blue-400" size={20} />
                )}
                {/* {platform === "twitter" && (
                  <FaTwitter className="text-blue-400" size={20} />
                )} */}
                {/* {platform === "instagram" && (
                  <FaInstagram className="text-pink-500" size={20} />
                )} */}
                {/* {platform === "linkedin" && (
                  <FaLinkedin className="text-blue-700" size={20} />
                )} */}
                {platform === "youtube" && (
                  <FaYoutube className="text-red-600" size={20} />
                )}
                <input
                  type="text"
                  className="input-field"
                  value={url}
                  onChange={(e) =>
                    setSocialLinks({ ...socialLinks, [platform]: e.target.value })
                  }
                  placeholder={`Enter ${platform} URL`}
                />
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500"
                  >
                    <FaLink size={12}/>
                  </a>
                )}
              </div>
            ))}
            </div>
          </div>

          {/* Copyright List Items */}
          <div className="bg-white shadow-sm rounded-lg mt-5">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                  <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Copyright List Items
                  </h2>
              </div>
            <div className="p-5">
            {listItems.map((item, index) => (
              <div key={index} className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  className="input-field"
                  value={item.label}
                  onChange={(e) => {
                    const updatedItems = [...listItems];
                    updatedItems[index].label = e.target.value;
                    setListItems(updatedItems);
                  }}
                  placeholder="Item label"
                />
                <input
                  type="text"
                  className="input-field"
                  value={item.url}
                  onChange={(e) => {
                    const updatedItems = [...listItems];
                    updatedItems[index].url = e.target.value;
                    setListItems(updatedItems);
                  }}
                  placeholder="URL"
                />
                {item.url && (
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500"
                  >
                    <FaLink size={12}/>
                  </a>
                )}
                <button
                  type="button"
                  onClick={() =>
                    setListItems(listItems.filter((_, i) => i !== index))
                  }
                  className="text-red-500"
                >
                  <FaTrash size={12}/>
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={() => setListItems([...listItems, { label: "", url: "" }])}
              className="mt-3 text-blue-600 text-sm flex items-center gap-1"
            >
              <FaPlus size={12}/> Add List Item
            </button>
            </div>
          </div>
        </div>

        {/* Save Changes Button */}
        <button
          type="submit"
         className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200 mt-4"
        >
          Save Changes
        </button>
      </form>
    </div>
  );
};

export default Footer;
